import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuthContext } from '../../contexts/AuthContext';
import SalePageNavbar from '../../components/SalePageNavbar';
import PaymentMethodSelector from '../../components/PaymentMethodSelector';
import QRPromptPayPayment from '../../components/QRPromptPayPayment';
import { Package, ArrowLeft, Tag, X, CheckCircle } from 'lucide-react';
import { collection, addDoc, serverTimestamp, getDocs, doc, getDoc, query, where, updateDoc, increment } from 'firebase/firestore';
import { db } from '../../lib/firebase';

export default function SalePageCheckoutNew() {
  const { user } = useAuthContext();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [checkoutData, setCheckoutData] = useState<any>(null);
  const [paymentMethod, setPaymentMethod] = useState<'qr_promptpay' | 'credit_card'>('qr_promptpay');
  const [showQRPayment, setShowQRPayment] = useState(false);
  const [discountCode, setDiscountCode] = useState('');
  const [appliedDiscount, setAppliedDiscount] = useState<{id: string, code: string, percentage: number, amount: number} | null>(null);
  const [discountError, setDiscountError] = useState('');
  const [checkingDiscount, setCheckingDiscount] = useState(false);

  useEffect(() => {
    if (!user) {
      const locationState = window.history.state;
      if (locationState?.amount !== undefined) {
        sessionStorage.setItem('pendingCheckoutData', JSON.stringify(locationState));
      }
      navigate('/login?redirect=/salepage/checkout-new');
      return;
    }

    // Try to get checkout data from multiple sources
    let checkoutDataFromState = null;
    
    // 1. Check localStorage (from /renew page)
    const localStorageData = localStorage.getItem('salePageCheckout');
    if (localStorageData) {
      const parsedData = JSON.parse(localStorageData);
      checkoutDataFromState = {
        plan: parsedData.plan,
        planName: parsedData.planName,
        price: parsedData.price,
        billingPeriod: parsedData.billingCycle || parsedData.billingPeriod || 'monthly',
        basePrice: parsedData.basePrice,
        hasPathUrl: parsedData.hasPathUrl || false,
        hasSubdomain: parsedData.hasSubdomain || false,
        username: parsedData.username || '',
        isRenewal: parsedData.isRenewal || false,
        isUpgrade: parsedData.isUpgrade || false
      };
      localStorage.removeItem('salePageCheckout');
    }
    // 2. Check window.history.state (from billing select page)
    else {
      const locationState = window.history.state;
      if (locationState?.amount !== undefined) {
        checkoutDataFromState = {
          plan: locationState.plan || (locationState.hasPathUrl || locationState.hasSubdomain ? 'premium' : 'basic'),
          planName: locationState.planName || (locationState.plan === 'premium' ? 'Premium' : 'Basic'),
          price: locationState.amount,
          billingPeriod: locationState.billingPeriod || 'monthly',
          hasPathUrl: locationState.hasPathUrl || false,
          hasSubdomain: locationState.hasSubdomain || false,
          username: locationState.username || '',
          isRenewal: false
        };
      } else {
        // 3. Check sessionStorage (fallback)
        const pendingData = sessionStorage.getItem('pendingCheckoutData');
        if (pendingData) {
          const parsedData = JSON.parse(pendingData);
          checkoutDataFromState = {
            plan: parsedData.plan || (parsedData.hasPathUrl || parsedData.hasSubdomain ? 'premium' : 'basic'),
            planName: parsedData.planName || (parsedData.plan === 'premium' ? 'Premium' : 'Basic'),
            price: parsedData.amount,
            billingPeriod: parsedData.billingPeriod || 'monthly',
            hasPathUrl: parsedData.hasPathUrl || false,
            hasSubdomain: parsedData.hasSubdomain || false,
            username: parsedData.username || '',
            isRenewal: false
          };
          sessionStorage.removeItem('pendingCheckoutData');
        }
      }
    }

    if (checkoutDataFromState) {
      setCheckoutData(checkoutDataFromState);
    } else {
      navigate('/salepage/pricing');
    }
  }, [user, navigate]);

  const handleApplyDiscount = async () => {
    if (!discountCode.trim()) {
      setDiscountError('กรุณากรอกรหัสส่วนลด');
      return;
    }

    setCheckingDiscount(true);
    setDiscountError('');

    try {
      const discountsQuery = query(
        collection(db, 'discountCodes'),
        where('code', '==', discountCode.trim().toUpperCase()),
        where('isActive', '==', true)
      );
      const discountsSnap = await getDocs(discountsQuery);

      if (discountsSnap.empty) {
        setDiscountError('รหัสส่วนลดไม่ถูกต้องหรือหมดอายุ');
        setCheckingDiscount(false);
        return;
      }

      const discountDoc = discountsSnap.docs[0];
      const discountData = discountDoc.data();

      if (discountData.expiresAt && discountData.expiresAt.toDate() < new Date()) {
        setDiscountError('รหัสส่วนลดหมดอายุแล้ว');
        setCheckingDiscount(false);
        return;
      }

      if (discountData.usageLimit && discountData.usedCount >= discountData.usageLimit) {
        setDiscountError('รหัสส่วนลดถูกใช้งานครบแล้ว');
        setCheckingDiscount(false);
        return;
      }

      const discountAmount = Math.round((checkoutData.price * discountData.percentage) / 100);
      setAppliedDiscount({
        id: discountDoc.id,
        code: discountData.code,
        percentage: discountData.percentage,
        amount: discountAmount
      });
      setDiscountError('');
    } catch (error) {
      console.error('Error applying discount:', error);
      setDiscountError('เกิดข้อผิดพลาดในการตรวจสอบรหัสส่วนลด');
    } finally {
      setCheckingDiscount(false);
    }
  };

  const handleRemoveDiscount = () => {
    setAppliedDiscount(null);
    setDiscountCode('');
    setDiscountError('');
  };

  const getFinalAmount = () => {
    if (!checkoutData) return 0;
    return appliedDiscount 
      ? checkoutData.price - appliedDiscount.amount 
      : checkoutData.price;
  };

  const handlePaymentMethodChange = (method: 'qr_promptpay' | 'credit_card') => {
    setPaymentMethod(method);
    setShowQRPayment(false);
  };

  const handleProceedToPayment = () => {
    if (paymentMethod === 'qr_promptpay') {
      setShowQRPayment(true);
    } else {
      // Credit Card - Coming Soon
      alert('ระบบบัตรเครดิตกำลังจะเปิดให้บริการเร็วๆ นี้');
    }
  };

  const handlePaymentSuccess = async (chargeId: string) => {
    try {
      const paymentData = {
        userId: user!.uid,
        userEmail: user!.email,
        plan: checkoutData.plan,
        planName: checkoutData.planName,
        amount: getFinalAmount(),
        originalAmount: checkoutData.price,
        discountCode: appliedDiscount?.code || null,
        discountAmount: appliedDiscount?.amount || 0,
        billingPeriod: checkoutData.billingPeriod,
        hasPathUrl: checkoutData.hasPathUrl,
        hasSubdomain: checkoutData.hasSubdomain,
        username: checkoutData.username,
        paymentMethod: 'promptpay',
        chargeId: chargeId,
        status: 'verified',
        createdAt: serverTimestamp(),
        verifiedAt: serverTimestamp()
      };

      await addDoc(collection(db, 'salePagePaymentNotifications'), paymentData);

      if (appliedDiscount) {
        const discountRef = doc(db, 'discountCodes', appliedDiscount.id);
        await updateDoc(discountRef, {
          usedCount: increment(1)
        });
      }

      navigate('/salepage/payment-success');
    } catch (error) {
      console.error('Error saving payment:', error);
      alert('เกิดข้อผิดพลาดในการบันทึกข้อมูลการชำระเงิน');
    }
  };

  const handlePaymentError = (error: string) => {
    console.error('Payment error:', error);
    alert(error);
    setShowQRPayment(false);
  };

  if (!checkoutData) {
    return (
      <div className="min-h-screen bg-[#F3F8F8] flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">กำลังโหลด...</p>
        </div>
      </div>
    );
  }

  if (showQRPayment) {
    return (
      <div className="min-h-screen bg-[#F3F8F8]">
        <SalePageNavbar />
        <div className="max-w-4xl mx-auto px-4 py-8">
          <QRPromptPayPayment
            amount={getFinalAmount()}
            planName={checkoutData.planName}
            onPaymentSuccess={handlePaymentSuccess}
            onPaymentError={handlePaymentError}
            onCancel={() => setShowQRPayment(false)}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F3F8F8]">
      <SalePageNavbar />
      
      <div className="max-w-6xl mx-auto px-4 py-8">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-6 transition-colors"
        >
          <ArrowLeft className="w-5 h-5" />
          กลับ
        </button>

        <h1 className="text-3xl font-bold text-gray-900 mb-2 flex items-center gap-3">
          <Package className="w-8 h-8 text-emerald-600" />
          สรุปการชำระเงิน
        </h1>
        <p className="text-gray-600 mb-8">ตรวจสอบรายการและชำระเงินของคุณ</p>

        <div className="grid lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-6">
            {/* Package Info */}
            <div className="bg-white rounded-2xl shadow-lg p-6 border-2 border-emerald-200">
              <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                <Package className="w-5 h-5 text-emerald-600" />
                รายการที่เลือก
              </h2>
              
              <div className="bg-gradient-to-br from-emerald-50 to-teal-50 rounded-lg p-4 mb-4">
                <div className="flex items-center justify-between mb-2">
                  <div>
                    <p className="text-sm text-gray-600">แพ็คเกจ Salepage</p>
                    <p className="text-xl font-bold text-gray-900">{checkoutData.planName}</p>
                    <p className="text-sm text-gray-600 mt-1">{checkoutData.billingPeriod === 'monthly' ? 'รายเดือน' : 'รายปี'}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-2xl font-bold text-emerald-600">฿{checkoutData.price.toLocaleString()}</p>
                    <p className="text-xs text-gray-500">ต่อเดือน</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Discount Code */}
            <div className="bg-white rounded-2xl shadow-lg p-6 border-2 border-gray-200">
              <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                <Tag className="w-5 h-5 text-blue-600" />
                รหัสส่วนลด
              </h2>
              
              {!appliedDiscount ? (
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={discountCode}
                    onChange={(e) => setDiscountCode(e.target.value.toUpperCase())}
                    placeholder="กรอกรหัสส่วนลด"
                    className="flex-1 px-4 py-2 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-blue-500"
                    disabled={checkingDiscount}
                  />
                  <button
                    onClick={handleApplyDiscount}
                    disabled={checkingDiscount}
                    className="px-6 py-2 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 disabled:bg-gray-400 transition-colors"
                  >
                    {checkingDiscount ? 'กำลังตรวจสอบ...' : 'ใช้'}
                  </button>
                </div>
              ) : (
                <div className="bg-green-50 border-2 border-green-200 rounded-lg p-4 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-green-600" />
                    <div>
                      <p className="font-semibold text-gray-900">{appliedDiscount.code}</p>
                      <p className="text-sm text-green-600">ส่วนลด {appliedDiscount.percentage}% (฿{appliedDiscount.amount.toLocaleString()})</p>
                    </div>
                  </div>
                  <button
                    onClick={handleRemoveDiscount}
                    className="text-gray-400 hover:text-red-600 transition-colors"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
              )}
              
              {discountError && (
                <p className="text-sm text-red-600 mt-2">{discountError}</p>
              )}
            </div>

            {/* Payment Method */}
            <div className="bg-white rounded-2xl shadow-lg p-6 border-2 border-gray-200">
              <PaymentMethodSelector
                selectedMethod={paymentMethod}
                onMethodChange={handlePaymentMethodChange}
              />
            </div>
          </div>

          {/* Summary */}
          <div className="lg:col-span-1">
            <div className="bg-gradient-to-br from-emerald-50 to-teal-50 rounded-2xl shadow-lg p-6 border-2 border-emerald-200 sticky top-24">
              <h2 className="text-xl font-bold text-gray-900 mb-4">สรุปยอดชำระ</h2>
              
              <div className="space-y-3 mb-4">
                <div className="flex items-center justify-between">
                  <span className="text-gray-600">แพ็คเกจ {checkoutData.planName}</span>
                  <span className="font-semibold">฿{checkoutData.price.toLocaleString()}</span>
                </div>
                
                <div className="flex items-center justify-between text-emerald-600">
                  <span>รายละเอียด</span>
                  <span className="text-sm">{checkoutData.billingPeriod === 'monthly' ? 'รายเดือน' : 'รายปี'}</span>
                </div>

                {appliedDiscount && (
                  <div className="flex items-center justify-between text-green-600">
                    <span>ส่วนลด ({appliedDiscount.percentage}%)</span>
                    <span className="font-semibold">-฿{appliedDiscount.amount.toLocaleString()}</span>
                  </div>
                )}
              </div>

              <div className="border-t-2 border-emerald-200 pt-4 mb-6">
                <div className="flex items-center justify-between">
                  <span className="text-lg font-bold text-gray-900">ยอดรวมทั้งหมด</span>
                  <span className="text-2xl font-bold text-emerald-600">
                    ฿{getFinalAmount().toLocaleString()}
                  </span>
                </div>
                <p className="text-xs text-gray-500 mt-1">ต่อเดือน</p>
              </div>

              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mb-4">
                <p className="text-xs text-yellow-800 font-semibold mb-2">ข้อควรทราบ:</p>
                <ul className="text-xs text-yellow-700 space-y-1">
                  <li>• โอนเงินผ่าน QR Code ได้ทันที</li>
                  <li>• ระบบตรวจสอบการชำระเงินอัตโนมัติ</li>
                  <li>• ยืนยันการชำระภายใน 24 ชม.</li>
                  <li>• สามารถยกเลิกได้ทุกเมื่อ</li>
                </ul>
              </div>

              <button
                onClick={handleProceedToPayment}
                disabled={paymentMethod === 'credit_card'}
                className="w-full py-4 bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 disabled:from-gray-400 disabled:to-gray-500 text-white rounded-xl font-bold text-lg shadow-lg transition-all"
              >
                {paymentMethod === 'credit_card' ? 'เร็วๆ นี้' : 'แจ้งชำระเงิน'}
              </button>
              
              <p className="text-xs text-gray-500 text-center mt-3">
                คลิกปุ่มเพื่อดำเนินการชำระเงินและยอมรับเงื่อนไขการใช้บริการ
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
